import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft,
  Play,
  FileText,
  Clock,
  Sparkles,
  ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  sales: "from-primary to-purple-500",
  educational: "from-accent-success to-teal-500",
  marketing: "from-accent-warning to-orange-500",
  explainer: "from-accent-neon to-blue-500"
};

export default function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      // Create project with template
      const response = await axios.post(`${API}/projects`, {
        title: `${template.name} - New Project`,
        description: template.description,
        video_type: template.category,
        target_duration: template.scenes_count * 10
      });

      // Update with script template
      await axios.put(`${API}/projects/${response.data.id}`, {
        script: template.script_template
      });

      toast.success("Project created from template!");
      navigate(`/studio/${response.data.id}`);
    } catch (error) {
      toast.error("Failed to create project");
    }
  };

  const categories = ["all", ...new Set(templates.map(t => t.category))];
  const filteredTemplates = selectedCategory === "all" 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-text-secondary hover:text-text-primary"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
          <Button 
            className="bg-primary hover:bg-primary-hover text-white"
            onClick={() => navigate("/create")}
            data-testid="create-from-scratch"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Create from Scratch
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading font-bold text-4xl text-text-primary mb-4">
              Video Templates
            </h1>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Start with professionally crafted templates for sales, educational, and marketing videos
            </p>
          </motion.div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat)}
              className={`capitalize ${
                selectedCategory === cat 
                  ? "bg-primary hover:bg-primary-hover text-white" 
                  : "border-border hover:bg-background-paper"
              }`}
              data-testid={`filter-${cat}`}
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="loading-spinner w-8 h-8" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className="bg-background-paper border-border hover:border-primary/50 transition-all duration-300 overflow-hidden group cursor-pointer"
                  onClick={() => handleUseTemplate(template)}
                  data-testid={`template-${template.id}`}
                >
                  <CardContent className="p-0">
                    {/* Thumbnail */}
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={template.thumbnail}
                        alt={template.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                      
                      {/* Category badge */}
                      <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${categoryColors[template.category]}`}>
                        {template.category}
                      </div>

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                          <Play className="w-6 h-6 text-primary fill-primary ml-1" />
                        </div>
                      </div>
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-lg text-text-primary mb-2 group-hover:text-primary transition-colors">
                        {template.name}
                      </h3>
                      <p className="text-sm text-text-muted mb-4 line-clamp-2">
                        {template.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs text-text-muted">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {template.scenes_count} scenes
                          </span>
                        </div>
                        <Button
                          size="sm"
                          className="bg-primary/10 text-primary hover:bg-primary hover:text-white"
                          data-testid={`use-template-${template.id}`}
                        >
                          Use Template
                          <ArrowRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && filteredTemplates.length === 0 && (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-text-muted mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-text-primary mb-2">
              No templates found
            </h3>
            <p className="text-text-secondary">
              Try selecting a different category
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
